'use client'

import Image from 'next/image'
import { Pause, Play } from 'lucide-react'
import { useEffect, useState, useSyncExternalStore } from 'react'
import type { HeroCollageFrame, HeroCollageLabels } from '@/lib/santa-video/landing-model'
import { cn } from '@/lib/utils'

const ROTATION_MS = 4000

/**
 * Dónde va cada fotograma respecto al de delante: el siguiente asoma a la
 * derecha, el anterior a la izquierda y el resto espera oculto detrás. Solo la
 * de delante es legible; las otras dos dan la idea de «hay más momentos».
 */
const POSITION = {
  front: 'z-30 translate-x-0 rotate-0 scale-100 opacity-100',
  next: 'z-20 translate-x-[16%] rotate-[7deg] scale-[0.88] opacity-80 brightness-75',
  previous: 'z-10 -translate-x-[16%] -rotate-[7deg] scale-[0.88] opacity-80 brightness-75',
  hidden: 'z-0 translate-x-0 rotate-0 scale-75 opacity-0',
} as const

function positionOf(index: number, active: number, count: number): keyof typeof POSITION {
  const offset = (index - active + count) % count
  if (offset === 0) return 'front'
  if (offset === 1) return 'next'
  if (offset === count - 1) return 'previous'
  return 'hidden'
}

const REDUCED_MOTION = '(prefers-reduced-motion: reduce)'

function subscribeReducedMotion(onChange: () => void): () => void {
  const query = window.matchMedia?.(REDUCED_MOTION)
  query?.addEventListener('change', onChange)
  return () => query?.removeEventListener('change', onChange)
}

/** La preferencia solo existe en el cliente: el servidor pinta el primer fotograma, quieto. */
function usePrefersReducedMotion(): boolean {
  return useSyncExternalStore(subscribeReducedMotion, () => window.matchMedia?.(REDUCED_MOTION).matches === true, () => false)
}

/** Un fotograma de la pila, en su posición, con su etiqueta de historia y su pie encima. */
function CollageFrame({ frame, position, lcp }: { frame: HeroCollageFrame; position: keyof typeof POSITION; lcp: boolean }) {
  return (
    <div
      aria-hidden={position !== 'front'}
      className={cn(
        'absolute inset-0 overflow-hidden rounded-3xl border-4 border-white/25 bg-black shadow-2xl transition-all duration-700 ease-out motion-reduce:transition-none',
        POSITION[position],
      )}
    >
      <Image
        alt={frame.caption}
        className="object-cover"
        fill
        sizes="300px"
        src={frame.src}
        // El candidato a LCP; `priority` está deprecado en Next 16 (se recomienda esto).
        {...(lcp ? { loading: 'eager', fetchPriority: 'high' } as const : {})}
      />
      <div className="absolute inset-x-0 bottom-0 flex flex-col items-start gap-2 bg-gradient-to-t from-black/85 via-black/50 to-transparent px-4 pb-4 pt-16 text-left">
        <span className="rounded-full bg-amber-200 px-3 py-0.5 text-xs font-semibold uppercase tracking-wide text-red-900">{frame.tag}</span>
        <p className="text-base font-semibold leading-snug text-white">{frame.caption}</p>
      </div>
    </div>
  )
}

/** Los puntos para saltar a un fotograma y el botón de pausa (WCAG 2.2.2). */
function CollageControls({ frames, active, paused, labels, onSelect, onTogglePause }: {
  frames: readonly HeroCollageFrame[]
  active: number
  paused: boolean
  labels: HeroCollageLabels
  onSelect: (index: number) => void
  onTogglePause: () => void
}) {
  return (
    <div className="flex items-center gap-3">
      <ol className="flex items-center gap-1">
        {frames.map((frame, index) => (
          <li key={frame.id}>
            <button
              aria-current={index === active}
              aria-label={labels.goTo.replace('{n}', String(index + 1))}
              className="flex h-6 w-6 items-center justify-center"
              onClick={() => onSelect(index)}
              type="button"
            >
              <span className={cn('block h-2 rounded-full transition-all motion-reduce:transition-none', index === active ? 'w-5 bg-amber-200' : 'w-2 bg-white/50')} />
            </button>
          </li>
        ))}
      </ol>
      <button
        aria-label={paused ? labels.play : labels.pause}
        className="flex h-8 w-8 items-center justify-center rounded-full border border-white/40 bg-white/10 text-white hover:bg-white/20"
        onClick={onTogglePause}
        type="button"
      >
        {paused ? <Play aria-hidden className="h-4 w-4" /> : <Pause aria-hidden className="h-4 w-4" />}
      </button>
    </div>
  )
}

/**
 * El collage del hero: una pila de fotogramas verticales de los vídeos que
 * rota sola, cada uno con el pie de lo que pasa en ese momento. Sustituye al
 * vídeo de ejemplo del hero ahora que hay dos historias: los ejemplos
 * reproducibles viven en sus fichas.
 *
 * Contenido que se mueve solo más de cinco segundos: lleva pausa (WCAG 2.2.2),
 * se detiene mientras el puntero o el foco están encima, y con
 * `prefers-reduced-motion` nace parado. Los puntos saltan a un fotograma.
 */
export function HeroCollage({ frames, labels }: { frames: readonly HeroCollageFrame[]; labels: HeroCollageLabels }) {
  const [active, setActive] = useState(0)
  // `null` hasta que el visitante pulse pausa o reanudar: hasta entonces manda su preferencia de movimiento.
  const [chosenPause, setChosenPause] = useState<boolean | null>(null)
  const [held, setHeld] = useState(false)
  const reducedMotion = usePrefersReducedMotion()
  const paused = chosenPause ?? reducedMotion
  const count = frames.length

  useEffect(() => {
    if (paused || held || count < 2) return
    const timer = window.setInterval(() => setActive((current) => (current + 1) % count), ROTATION_MS)
    return () => window.clearInterval(timer)
  }, [paused, held, count])

  if (count === 0) return null

  return (
    <figure
      aria-label={labels.label}
      className="flex w-full min-w-0 flex-col items-center gap-5"
      onBlur={() => setHeld(false)}
      onFocus={() => setHeld(true)}
      onMouseEnter={() => setHeld(true)}
      onMouseLeave={() => setHeld(false)}
      role="group"
    >
      <div className="relative aspect-[9/16] w-[72%] max-w-[300px]">
        {frames.map((frame, index) => (
          <CollageFrame frame={frame} key={frame.id} lcp={index === 0} position={positionOf(index, active, count)} />
        ))}
      </div>
      <figcaption className="sr-only">{frames[active].caption}</figcaption>
      {count > 1 && (
        <CollageControls
          active={active}
          frames={frames}
          labels={labels}
          onSelect={setActive}
          onTogglePause={() => setChosenPause(!paused)}
          paused={paused}
        />
      )}
    </figure>
  )
}
